import { useEffect } from 'react'
import { useQueryClient, useQuery, type QueryClient } from '@tanstack/react-query'
import { apiGet, type SessionState } from './api.ts'

/**
 * The session is the app's root state.
 *
 * Everything else renders underneath it, so it lives in one query that the
 * whole app reads and every auth action invalidates. There is no client-side
 * copy of "am I logged in" — the server answers that, and the answer is the
 * only thing the UI trusts.
 */

export const SESSION_QUERY_KEY = ['auth', 'session'] as const

export function useSession() {
  return useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: () => apiGet<SessionState>('/auth/session'),
    // A revoked session must not linger behind a cached "authenticated".
    staleTime: 0,
    retry: false,
  })
}

/** Call after any action that changes the session: login, MFA, enrol, logout. */
export function useRefreshSession() {
  const client = useQueryClient()
  return () => client.invalidateQueries({ queryKey: SESSION_QUERY_KEY })
}

/** Drops every cached query. Used on logout so no voter data survives it. */
export function useClearSession() {
  const client = useQueryClient()
  return () => client.clear()
}

/*
 * The 15-minute idle rule (CCR §19012(b)(2)(C), migration 0096).
 *
 * The server is the authority: a session unused for 15 minutes is revoked the
 * next time it is presented. The browser does two things around that:
 *
 *   - Real use keeps you signed in. Reading a long page or filling a long form
 *     makes no requests, so while someone is typing, clicking or scrolling, a
 *     ping goes once a minute. Screens that refresh themselves mark those
 *     requests as background, so they never count.
 *   - Nobody at the keyboard for 15 minutes: drop everything cached (voter
 *     rows included) and ask the server again, which answers "anonymous", and
 *     the sign-in screen replaces whatever was showing. A screen left open
 *     must not keep displaying data after its session is gone.
 */

const IDLE_MS = 15 * 60_000
let signedOutNotice: string | null = null

/** Read once by the sign-in screen: why you are looking at it. */
export function takeSignedOutNotice(): string | null {
  const n = signedOutNotice
  signedOutNotice = null
  return n
}

/** Any request refused as signed out: forget cached data and re-ask who we are. */
export function onUnauthenticated(client: QueryClient) {
  const session = client.getQueryData<SessionState>(SESSION_QUERY_KEY)
  if (session?.state !== 'authenticated') return
  signedOutNotice = 'You were signed out. Sign in again to continue.'
  void dropEverything(client)
}

/*
 * Re-ask the server first, so the sign-in screen replaces the product, then
 * drop every other cached query. The other order removes the session query
 * itself and the re-ask goes nowhere.
 */
async function dropEverything(client: QueryClient) {
  await client.refetchQueries({ queryKey: SESSION_QUERY_KEY })
  client.removeQueries({ predicate: (q) => q.queryKey[0] !== SESSION_QUERY_KEY[0] })
}

export function useIdleSignOut(active: boolean) {
  const client = useQueryClient()
  useEffect(() => {
    if (!active) return
    let lastInput = Date.now()
    let lastPing = Date.now()
    const mark = () => {
      lastInput = Date.now()
    }
    const events = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll'] as const
    for (const e of events) window.addEventListener(e, mark, { passive: true, capture: true })

    const tick = window.setInterval(() => {
      const now = Date.now()
      if (now - lastInput >= IDLE_MS) {
        signedOutNotice = 'You were signed out after 15 minutes without activity.'
        lastInput = now // one sign-out per idle stretch, not one every tick
        void dropEverything(client)
        return
      }
      if (lastInput > lastPing && now - lastPing >= 60_000) {
        lastPing = now
        void apiGet('/auth/me').catch(() => onUnauthenticated(client))
      }
    }, 15_000)

    return () => {
      window.clearInterval(tick)
      for (const e of events) window.removeEventListener(e, mark, { capture: true })
    }
  }, [active, client])
}
